// Buscador puntual de tercero por NIT en el ERP externo (Alegra/Siigo)
// para autocompletar el formulario de "Nuevo Proveedor" o sincronizar uno
// existente sin idContable.
//
// Diferencias con SelectorProveedorTercero (modulo financiero):
//  - No tiene busqueda local previa (eso ya lo hace la pantalla con su tabla).
//  - No abre captura manual (el modal ya es la captura manual).
//  - Solo dos estados: input vacio o resultado encontrado.
//  - Valida que el NIT sea solo digitos antes de permitir buscar.
//
// Props:
//   onAutocompletar(itemERP)   // se invoca con {nombre, nit, idContable, email, telefono}
//   disabled                   // true mientras el padre guarda
//   nitInicial                 // util en modo editar: si el padre ya tiene nit, prefilear

import React, { useState } from 'react';
import { Form, Button, Alert, Badge, InputGroup, Spinner } from 'react-bootstrap';
import { FaSearch, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import { useProveedorExternoPorNit } from '../../hooks/useProveedoresExternos';

const NIT_REGEX = /^\d+$/;
const NIT_MINIMO = 5;

const BuscadorERPParaNuevoProveedor = ({ onAutocompletar, disabled = false, nitInicial = '' }) => {
  const [nit, setNit] = useState(nitInicial);
  const [nitConfirmado, setNitConfirmado] = useState('');
  const [errorValidacion, setErrorValidacion] = useState(null);

  const {
    data: dataExt,
    isFetching: buscando,
    isError: errorBusqueda,
    error: errBusquedaObj
  } = useProveedorExternoPorNit(nitConfirmado);

  const limpiezaSugerida = (raw) => raw.replace(/[\s.-]/g, '');

  const handleChange = (e) => {
    const v = e.target.value;
    setNit(v);
    setErrorValidacion(null);
  };

  const handleBuscar = () => {
    const valor = (nit || '').trim();

    // Si el usuario pego con puntos/guiones, sugerirle la version limpia
    const sugerencia = limpiezaSugerida(valor);
    if (valor !== sugerencia && NIT_REGEX.test(sugerencia)) {
      setErrorValidacion(`El NIT debe ser solo digitos sin puntos ni guiones. Sugerencia: ${sugerencia}`);
      return;
    }

    if (!valor) {
      setErrorValidacion('Escribe un NIT para buscar.');
      return;
    }
    if (!NIT_REGEX.test(valor)) {
      setErrorValidacion('El NIT debe contener solo numeros (sin puntos, guiones ni espacios).');
      return;
    }
    if (valor.length < NIT_MINIMO) {
      setErrorValidacion(`El NIT debe tener al menos ${NIT_MINIMO} digitos.`);
      return;
    }
    setErrorValidacion(null);
    setNitConfirmado(valor);
  };

  const resultados = dataExt?.proveedores || [];

  console.log(resultados)
  const providerNombre = dataExt?.providerNombre || 'el ERP';
  const yaBusco = nitConfirmado && !buscando;
  const noConfigurado = yaBusco && dataExt && dataExt.configurado === false;

  const handleSeleccionar = (item) => {
    onAutocompletar?.(item);
    // No limpiamos el nit del input para que el usuario vea cual selecciono
  };

  // NIT invalido (solo si el usuario ya intento buscar)
  const inputInvalido = nit.length > 0 && !NIT_REGEX.test(nit);

  return (
    <div className="border rounded p-3 mb-3 bg-light">
      <div className="d-flex align-items-center mb-2">
        <FaCheckCircle className="text-primary me-2" />
        <strong>Sincronizar con software contable</strong>
        <small className="text-muted ms-auto">opcional</small>
      </div>
      <small className="text-muted d-block mb-2">
        Si el tercero ya existe en tu ERP contable, busca por NIT y autocompletamos los campos.
      </small>

      <InputGroup size="sm">
        <Form.Control
          type="text"
          inputMode="numeric"
          placeholder="NIT (solo numeros, sin puntos ni guiones)"
          value={nit}
          onChange={handleChange}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleBuscar(); } }}
          isInvalid={inputInvalido}
          disabled={disabled || buscando}
        />
        <Button
          variant="primary"
          onClick={handleBuscar}
          disabled={disabled || buscando || !nit.trim()}
          title="Buscar este NIT en el ERP externo"
        >
          {buscando ? <Spinner animation="border" size="sm" /> : <><FaSearch className="me-1" /> Buscar</>}
        </Button>
      </InputGroup>

      {errorValidacion && (
        <Alert variant="warning" className="py-2 small mt-2 mb-0">
          <FaInfoCircle className="me-2" />
          {errorValidacion}
        </Alert>
      )}

      {errorBusqueda && (
        <Alert variant="danger" className="py-2 small mt-2 mb-0">
          Error consultando el ERP: {errBusquedaObj?.message || 'desconocido'}
        </Alert>
      )}

      {noConfigurado && (
        <Alert variant="light" className="py-2 small mt-2 mb-0 border">
          No hay proveedor contable configurado para terceros. Continua llenando el formulario manualmente.
        </Alert>
      )}

      {yaBusco && !errorBusqueda && !noConfigurado && resultados.length === 0 && (
        <Alert variant="warning" className="py-2 small mt-2 mb-0">
          NIT <code>{nitConfirmado}</code> no se encontro en {providerNombre}. Puedes llenar el formulario manualmente.
        </Alert>
      )}

      {resultados.length > 0 && (
        <div className="mt-2">
          <small className="text-muted d-block mb-1">
            Encontrado en {providerNombre}. Haz clic para autocompletar:
          </small>
          <div className="border rounded bg-white" style={{ maxHeight: 180, overflowY: 'auto' }}>
            {resultados.map((p, idx) => (
              <button
                key={p.idContable || p.nit || idx}
                type="button"
                className="d-flex justify-content-between align-items-center w-100 px-2 py-2 border-bottom"
                style={{ border: 'none', textAlign: 'left', background: 'transparent' }}
                onClick={() => handleSeleccionar(p)}
                disabled={disabled}
              >
                <div>
                  <strong>{p.nombre}</strong>
                  {p.nit && <code className="ms-2">{p.nit}</code>}
                </div>
                <Badge bg="info" pill>{providerNombre}</Badge>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BuscadorERPParaNuevoProveedor;
